import { partition } from 'ramda'

import { PathWithSkills } from 'api/fetchers/digital-profile/experience-path/fetchExperiencePathsCompareSelect'

export function isTokenExpired(expiration: number = 0) {
  const now = Math.floor(Date.now() / 1000)
  return expiration < now
}

export function adjustTextSize(
  containerElement: HTMLElement | null,
  textElement: HTMLElement | null,
  maxFontSizeParam?: number | undefined,
) {
  let minFontSize = 1 // Minimum font size
  let maxFontSize = maxFontSizeParam ? maxFontSizeParam : 170 // Maximum font size, start big enough
  let fontSize

  if (containerElement === null || textElement === null) return
  while (minFontSize <= maxFontSize) {
    fontSize = ((minFontSize + maxFontSize) / 2) | 0 // Integer division
    textElement.style.fontSize = fontSize + 'px'

    if (textElement.scrollWidth <= containerElement.offsetWidth) {
      minFontSize = fontSize + 1 // Try a bigger size
    } else {
      maxFontSize = fontSize - 1 // Try a smaller size
    }
  }

  // Set the font size to the last best fit
  textElement.style.fontSize = minFontSize - 1 + 'px'
}

const linearize = (c: number): number => {
  c = c / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

const calculateLuminance = (r: number, g: number, b: number): number => {
  const rLin = linearize(r)
  const gLin = linearize(g)
  const bLin = linearize(b)
  return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin
}

const contrastRatio = (l1: number, l2: number): number => {
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

const hexToRgb = (hex: string): [number, number, number] => {
  const normalizedHex = hex.replace('#', '')
  const bigint = parseInt(normalizedHex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return [r, g, b]
}

export const isBackgroundTooDarkForWhiteText = (hexColor: string): boolean => {
  const [r, g, b] = hexToRgb(hexColor)
  const luminanceBackground = calculateLuminance(r, g, b)
  const luminanceWhite = calculateLuminance(255, 255, 255)
  const ratio = contrastRatio(luminanceBackground, luminanceWhite)
  return ratio < 4.5 // Use 3.0 for large text
}

// MAKE LIGHTER COLOR BY PERCENTAGE

function rgbToHex(r: number, g: number, b: number): string {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0,
    s = 0,
    l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return [h, s, l]
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number, g: number, b: number

  if (s === 0) {
    r = g = b = l // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
}

export function lightenHexColor(hex: string, percent: number): string {
  let [r, g, b] = hexToRgb(hex)
  let [h, s, l] = rgbToHsl(r, g, b)

  const myL = Math.min(1, l + percent / 100)

  const [nr, ng, nb] = hslToRgb(h, s, myL)

  return rgbToHex(nr, ng, nb)
}

export function reorderPaths(paths: PathWithSkills[]): PathWithSkills[] {
  if (paths.length <= 1) {
    // no change if 0 or 1 item
    return paths
  }

  // Partition into [preferredList, nonPreferredList]
  const [preferredList, nonPreferredList] = partition(p => p.path.preferred === true, paths)

  // If there's no preferred item, just return original
  if (preferredList.length === 0) {
    return paths
  }

  const preferredItem = preferredList[0]

  switch (paths.length) {
    case 2:
      // [nonPreferred, preferred]
      return [nonPreferredList[0], preferredItem]
    case 3:
      // [nonPreferred(0), preferred, nonPreferred(1)]
      return [nonPreferredList[0], preferredItem, nonPreferredList[1]]
    default:
      // If length is outside of 1-3, do nothing special
      return paths
  }
}

export function arrayMove(arr: undefined[], old_index: any, new_index: number) {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1
    while (k--) {
      arr.push(undefined)
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0])
  return arr // for testing
}
